import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { RecipesDataService, RecipesColumnsService } from '../../../services';
import { Recipe, RecipesResult } from '../../../interfaces';
import { Editor } from 'primeng/editor';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EditorService } from '../../../../services/editor/editor.service';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-recipe-form',
  templateUrl: './recipe-form.component.html',
  styleUrls: ['./recipe-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class RecipeFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public recipe: Recipe = {};
  public recipeForm: FormGroup = new FormGroup({});
  public recipes: Recipe[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;


  public Editor = ClassicEditor;
  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private recipesDataService: RecipesDataService,
    public recipesColumnsService: RecipesColumnsService,
    public editorService: EditorService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'RecipeFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Recipe - Edit');
        this.getRecipe(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.recipe !== 'undefined') {
      this.recipe = this.dynamicDialogConfig.data.recipe;
    }
    if (typeof this.recipe.id === 'undefined' || this.recipe.id === null) {
      this.recipe.id = null;
      this.recipe.startPublishing = moment().set({minute: 0, second: 0, millisecond: 0}).format('YYYY-MM-DD\THH:mm:ssZ');
      this.recipe.endPublishing = moment().set({minute: 0, second: 0, millisecond: 0}).format('YYYY-MM-DD\THH:mm:ssZ');
      this.recipe.publishDate = moment().set({minute: 0, second: 0, millisecond: 0}).format('YYYY-MM-DD\THH:mm:ssZ');
    }
    this.getRecipes();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'RecipeFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'RecipeFormComponent');
  }

  getRecipe(id: number): void {
    this.isLoading = true;
    this.recipesDataService.getRecipe(id).subscribe((response: Recipe|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.recipe = response;
        this.prepareFrom();
      }
    });
  }

  getRecipes(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.recipes !== 'undefined' && this.dynamicDialogConfig.data.recipes.length > 0) {
      this.recipes = this.dynamicDialogConfig.data.recipes;
      return;
    }
    this.recipesDataService.getRecipes(this.parameters).subscribe((response: RecipesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.recipes = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.recipe.startPublishing = moment(this.recipe.startPublishing).toDate();
    this.recipe.endPublishing = moment(this.recipe.endPublishing).toDate();
    this.recipe.publishDate = moment(this.recipe.publishDate).toDate();
    this.recipeForm = new FormGroup({
      id: new FormControl(this.recipe.id, []),
      title: new FormControl(this.recipe.title, []),
      titleAr: new FormControl(this.recipe.titleAr, []),
      titleFr: new FormControl(this.recipe.titleFr, []),
      slug: new FormControl(this.recipe.slug, []),
      slugAr: new FormControl(this.recipe.slugAr, []),
      slugFr: new FormControl(this.recipe.slugFr, []),
      picture: new FormControl(this.recipe.picture, []),
      content: new FormControl(this.recipe.content, []),
      contentAr: new FormControl(this.recipe.contentAr, []),
      contentFr: new FormControl(this.recipe.contentFr, []),
      isHeadline: new FormControl(this.recipe.isHeadline, [Validators.required, ]),
      startPublishing: new FormControl(this.recipe.startPublishing, []),
      endPublishing: new FormControl(this.recipe.endPublishing, []),
      publishDate: new FormControl(this.recipe.publishDate, [Validators.required, ]),
      status: new FormControl(this.recipe.status, [Validators.required, ]),
      totalPrints: new FormControl(this.recipe.totalPrints, []),
      totalHits: new FormControl(this.recipe.totalHits, []),
      totalComments: new FormControl(this.recipe.totalComments, []),
      totalRatings: new FormControl(this.recipe.totalRatings, []),
      averageRatings: new FormControl(this.recipe.averageRatings, []),
      totalLikes: new FormControl(this.recipe.totalLikes, []),
      totalDislikes: new FormControl(this.recipe.totalDislikes, []),
      totalBookmarks: new FormControl(this.recipe.totalBookmarks, []),
      isTop: new FormControl(this.recipe.isTop, [Validators.required, ]),
      isNew: new FormControl(this.recipe.isNew, [Validators.required, ]),
      ordering: new FormControl(this.recipe.ordering, []),
    });
  }

  list() {
    this.router.navigate(['/recipes']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.recipeForm.controls).forEach((field: any) => {
        const control = this.recipeForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.recipeForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.recipe = this.recipeForm.value;
    this.disableSubmit = true;
    console.log('recipe', this.recipe);
    if (typeof this.recipe.id !== 'undefined' && this.recipe.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.recipesDataService.updateRecipe(this.recipe).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('RecipeUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.recipesDataService.addRecipe(this.recipe).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('RecipeCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RecipeNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.recipeForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'recipes',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.recipeForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-recipe-form',
  templateUrl: './recipe-form.component.html',
  styleUrls: ['./recipe-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogRecipeFormComponent extends RecipeFormComponent {
}

